import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Building2, MapPin, Mail, Phone, ArrowRight, ArrowLeft, CheckCircle, 
  Loader2, Home, Target, Award, Shield, TrendingUp, Navigation,
  FileText, Star, Users, Zap, Send, Check, Globe, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import axios from 'axios';
import { addressApi } from '../services/api';
import AddressInput from '../components/AddressInput';
import { rememberPartner } from '../lib/partnerContext';
import { applyPwaBranding } from '../lib/pwa';
import { InstallPwaButton } from '../components/InstallPwaButton';
import { getContrastYIQ, getReadableColorOnWhite } from '../lib/colorUtils';
import RgpdCheckbox from '../components/RgpdCheckbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============ TRADUCTIONS FR / AR / EN ============
const TRANSLATIONS = {
  fr: {
    services_label: 'Nos Services', services_title: 'Choisissez votre service',
    hero_sub: 'Audit immobilier · Estimation · Conseil stratégique',
    cta_start: 'Démarrer mon projet', call_us: 'Nous appeler',
    certified: 'Certifié & Assuré', results: 'Résultats sous 48h',
    experts: 'Experts locaux', satisfaction: 'Satisfaction garantie',
    free: 'Gratuit', pro: 'Pro', vip: 'VIP',
    about_title: 'Notre histoire', contact_title: 'Nous contacter',
    send_btn: 'Envoyer', sending: 'Envoi...',
    email_placeholder: 'Votre email *', phone_placeholder: 'Votre téléphone',
    message_placeholder: 'Votre message...',
    contact_annonce: 'Contacter le partenaire',
    request_sent: 'Votre message a été envoyé !',
    request_sent_sub: 'Le partenaire vous contactera très prochainement.',
    vitrine_title: 'Nos annonces immobilières',
    vitrine_all: 'Tout voir', vitrine_sale: 'Vente', vitrine_rent: 'Location',
    back: 'Retour', new_estimate: 'Nouvelle estimation',
    our_biens: 'Nos Biens', continue: 'Continuer',
    get_estimate: 'Obtenir mon estimation',
    why_us_label: 'Pourquoi nous choisir',
    engagements_title: 'Nos engagements',
    gallery_label: 'Votre partenaire immobilier de confiance',
    cta_title: 'Prêt à commencer ?',
    cta_sub: "L'estimation est gratuite et instantanée.",
    cta_btn: 'Démarrer maintenant',
    stats_experience: "Ans d'expérience",
    stats_artisans: 'Artisans partenaires',
    stats_audits: 'Réalisations',
    stats_satisfaction: 'Satisfaction client',
    svc_estimation_label: 'Estimation Gratuite',
    svc_estimation_desc: 'Obtenez le prix au m² réel basé sur les ventes DVF',
    svc_radar_label: 'Radar Foncier Gratuit',
    svc_radar_desc: 'Recherchez une parcelle cadastrale',
    svc_audit_label: 'Audit Gratuite',
    svc_audit_desc: 'Analyse technique',
    svc_conseil_label: 'Conseil Stratégique & Travaux',
    svc_conseil_desc: "Suivi de travaux, stratégie d'investissement, médiation",
    eng_expertise_title: 'Expertise reconnue',
    eng_expertise_desc: '25 ans de terrain en bâtiment et transactions immobilières.',
    eng_rapide_title: 'Réponse rapide',
    eng_rapide_desc: 'Résultats et retours sous 48h, accompagnement personnalisé.',
    eng_certif_title: 'Réseau certifié',
    eng_certif_desc: 'Artisans RGE, partenaires bancaires et notariaux sélectionnés.',
    voir: 'Voir', biens_singular: 'bien disponible', biens_plural: 'biens disponibles',
    loyer_mensuel: 'Loyer mensuel', prix_vente: 'Prix de vente', surface_label: 'Surface',
    rooms_label: 'pièce', rooms_plural: 'pièces',
  },
  ar: {
    services_label: 'خدماتنا', services_title: 'اختر خدمتك',
    hero_sub: 'تقدير عقاري · استشارة · دعم متكامل',
    cta_start: 'ابدأ مشروعي', call_us: 'اتصل بنا',
    certified: 'معتمد ومؤمّن', results: 'نتائج خلال 48 ساعة',
    experts: 'خبراء محليون', satisfaction: 'ضمان الرضا',
    free: 'مجاني', pro: 'احترافي', vip: 'VIP',
    about_title: 'من نحن', contact_title: 'تواصل معنا',
    send_btn: 'إرسال', sending: 'جارٍ الإرسال...',
    email_placeholder: 'بريدك الإلكتروني *', phone_placeholder: 'رقم هاتفك',
    message_placeholder: 'رسالتك...',
    contact_annonce: 'التواصل مع الشريك',
    request_sent: 'تم إرسال رسالتك !',
    request_sent_sub: 'سيتواصل معك الشريك في أقرب وقت.',
    vitrine_title: 'إعلاناتنا العقارية',
    vitrine_all: 'الكل', vitrine_sale: 'بيع', vitrine_rent: 'إيجار',
    back: 'رجوع', new_estimate: 'تقدير جديد',
    our_biens: 'عقاراتنا', continue: 'متابعة',
    get_estimate: 'احصل على تقديري',
    why_us_label: 'لماذا تختارنا',
    engagements_title: 'التزاماتنا',
    gallery_label: 'شريكك العقاري الموثوق',
    cta_title: 'هل أنت مستعد ؟',
    cta_sub: 'التقدير مجاني وفوري.',
    cta_btn: 'ابدأ الآن',
    stats_experience: 'سنوات خبرة',
    stats_artisans: 'حرفيون شركاء',
    stats_audits: 'إنجازات',
    stats_satisfaction: 'رضا العملاء',
    svc_estimation_label: 'تقدير مجاني',
    svc_estimation_desc: 'احصل على السعر الحقيقي للمتر المربع بناءً على بيانات السوق',
    svc_radar_label: 'رادار العقارات',
    svc_radar_desc: 'اعثر على صاحب أي قطعة أرض في السجل العقاري',
    svc_audit_label: 'تدقيق شامل',
    svc_audit_desc: 'تحليل تقني ومالي كامل لعقارك',
    svc_conseil_label: 'استشارة استراتيجية',
    svc_conseil_desc: 'متابعة الأشغال، استراتيجية الاستثمار، الوساطة',
    eng_expertise_title: 'خبرة معترف بها',
    eng_expertise_desc: '25 عاماً من الخبرة في البناء والمعاملات العقارية.',
    eng_rapide_title: 'استجابة سريعة',
    eng_rapide_desc: 'نتائج وردود في غضون 48 ساعة، مع مرافقة شخصية.',
    eng_certif_title: 'شبكة معتمدة',
    eng_certif_desc: 'حرفيون ومشاورون ماليون وقانونيون مختارون بعناية.',
    voir: 'عرض', biens_singular: 'عقار متاح', biens_plural: 'عقارات متاحة',
    loyer_mensuel: 'الإيجار الشهري', prix_vente: 'سعر البيع', surface_label: 'المساحة',
    rooms_label: 'غرفة', rooms_plural: 'غرف',
  },
  en: {
    services_label: 'Our Services', services_title: 'Choose your service',
    hero_sub: 'Property Valuation · Advisory · Full Support',
    cta_start: 'Start my project', call_us: 'Call us',
    certified: 'Certified & Insured', results: 'Results within 48h',
    experts: 'Local Experts', satisfaction: 'Satisfaction Guaranteed',
    free: 'Free', pro: 'Pro', vip: 'VIP',
    about_title: 'About us', contact_title: 'Contact us',
    send_btn: 'Send', sending: 'Sending...',
    email_placeholder: 'Your email *', phone_placeholder: 'Your phone',
    message_placeholder: 'Your message...',
    contact_annonce: 'Contact the partner',
    request_sent: 'Your message has been sent!',
    request_sent_sub: 'The partner will contact you shortly.',
    vitrine_title: 'Our property listings',
    vitrine_all: 'All', vitrine_sale: 'For Sale', vitrine_rent: 'For Rent',
    back: 'Back', new_estimate: 'New estimate',
    our_biens: 'Our Properties', continue: 'Continue',
    get_estimate: 'Get my estimate',
    why_us_label: 'Why choose us',
    engagements_title: 'Our commitments',
    gallery_label: 'Your trusted real estate partner',
    cta_title: 'Ready to get started?',
    cta_sub: 'Property valuation is free and instant.',
    cta_btn: 'Start now',
    stats_experience: 'Years of experience',
    stats_artisans: 'Partner craftsmen',
    stats_audits: 'Completed projects',
    stats_satisfaction: 'Client satisfaction',
    svc_estimation_label: 'Free Valuation',
    svc_estimation_desc: 'Get the real price per m² based on official market data',
    svc_radar_label: 'Property Radar',
    svc_radar_desc: 'Find the owner of any cadastral plot',
    svc_audit_label: 'Full Audit',
    svc_audit_desc: 'Complete technical and financial analysis of your property',
    svc_conseil_label: 'Strategic Advice & Works',
    svc_conseil_desc: 'Works supervision, investment strategy, mediation',
    eng_expertise_title: 'Recognized expertise',
    eng_expertise_desc: '25 years of field experience in construction and real estate.',
    eng_rapide_title: 'Fast response',
    eng_rapide_desc: 'Results and feedback within 48h, personalized support.',
    eng_certif_title: 'Certified network',
    eng_certif_desc: 'RGE certified craftsmen, banking and legal partners.',
    voir: 'View', biens_singular: 'property available', biens_plural: 'properties available',
    loyer_mensuel: 'Monthly rent', prix_vente: 'Sale price', surface_label: 'Area',
    rooms_label: 'room', rooms_plural: 'rooms',
  },
};

const LANG_OPTIONS = [
  { key: 'fr', label: 'FR', flag: '🇫🇷' },
  { key: 'ar', label: 'عر', flag: '🇩🇿' },
  { key: 'en', label: 'EN', flag: '🇬🇧' },
];

// SERVICES utilise les clés de traduction dynamiques
const SERVICES_DEF = [
  { id: 'estimation', labelKey: 'svc_estimation_label', badgeKey: 'free', badgeColor: 'bg-emerald-100 text-emerald-700', descKey: 'svc_estimation_desc', icon: TrendingUp, iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600' },
  { id: 'radar', labelKey: 'svc_radar_label', badgeKey: 'free', badgeColor: 'bg-purple-100 text-purple-700', descKey: 'svc_radar_desc', icon: Navigation, iconBg: 'bg-purple-100', iconColor: 'text-purple-600' },
  { id: 'audit', labelKey: 'svc_audit_label', badgeKey: 'free', badgeColor: 'bg-gray-100 text-gray-700', descKey: 'svc_audit_desc', icon: Target, iconBg: 'bg-gray-100', iconColor: 'text-gray-800' },
  { id: 'conseil', labelKey: 'svc_conseil_label', badgeKey: 'vip', badgeColor: 'bg-amber-100 text-amber-700', descKey: 'svc_conseil_desc', icon: Shield, iconBg: 'bg-amber-100', iconColor: 'text-amber-600' },
];

const STATS_DEF = [
  { labelKey: 'stats_experience', value: '25+', icon: Award },
  { labelKey: 'stats_artisans', value: '50+', icon: Users },
  { labelKey: 'stats_audits', value: '1 200+', icon: FileText },
  { labelKey: 'stats_satisfaction', value: '4.9/5', icon: Star },
];

const PROPERTY_TYPES = [
  { id: 'appartement', label: 'Appartement', icon: '🏢' },
  { id: 'maison', label: 'Maison / Villa', icon: '🏠' },
  { id: 'immeuble', label: 'Immeuble', icon: '🏛️' },
  { id: 'terrain', label: 'Terrain', icon: '🌳' },
  { id: 'local_commercial', label: 'Local Commercial', icon: '🏪' },
  { id: 'bureau', label: 'Bureau / Locaux pro', icon: '🏢' },
  { id: 'entrepot', label: 'Entrepôt / Activité', icon: '🏭' },
  { id: 'parking', label: 'Parking / Box', icon: '🅿️' },
];

const PROPERTY_SUBCATEGORIES = {
  appartement: [
    { id: 'studio', label: 'Studio / T1' }, { id: 't2', label: 'T2' }, { id: 't3', label: 'T3' },
    { id: 't4', label: 'T4' }, { id: 't5_plus', label: 'T5 et +' }, { id: 'duplex', label: 'Duplex / Triplex' },
    { id: 'penthouse', label: 'Attique / Dernier étage' },
  ],
  maison: [
    { id: 'plain_pied', label: 'Plain-pied' }, { id: 'etage', label: 'Maison à étage' },
    { id: 'villa', label: 'Villa' }, { id: 'longere', label: 'Longère / Fermette' },
    { id: 'contemporaine', label: 'Contemporaine' }, { id: 'mitoyenne', label: 'Mitoyenne' },
  ],
  immeuble: [
    { id: 'rapport', label: 'Immeuble de rapport' }, { id: 'mixte', label: 'Mixte (habitation + commerce)' },
    { id: 'copropriete', label: 'Copropriété' },
  ],
  terrain: [
    { id: 'constructible', label: 'Constructible' }, { id: 'agricole', label: 'Agricole' },
    { id: 'loisir', label: 'Terrain de loisir' },
  ],
  local_commercial: [
    { id: 'boutique', label: 'Boutique / Commerce' }, { id: 'restaurant', label: 'Restaurant / Bar' },
    { id: 'bureau_local', label: 'Bureau' },
  ],
  bureau: [
    { id: 'open_space', label: 'Open Space' }, { id: 'cloisonne', label: 'Cloisonné' },
    { id: 'coworking', label: 'Coworking' },
  ],
  entrepot: [
    { id: 'stockage', label: 'Stockage' }, { id: 'activite', label: "Local d'activité" },
    { id: 'atelier', label: 'Atelier' },
  ],
  parking: [
    { id: 'souterrain', label: 'Souterrain' }, { id: 'exterieur', label: 'Extérieur' },
    { id: 'box_ferme', label: 'Box fermé' },
  ],
};

const VUE_OPTIONS = [
  { id: 'mer', label: 'Vue Mer', icon: '🌊', subtitle: 'PACA, Bretagne, Normandie, Atlantique',
    sub: [{ id: 'mer_apercu', label: 'Aperçu / Latérale', coef: 10 }, { id: 'mer_directe', label: 'Dégagée / Directe', coef: 20 }, { id: 'mer_panoramique', label: 'Panoramique / Exceptionnelle', coef: 35 }] },
  { id: 'lac', label: 'Vue Lac', icon: '💧', subtitle: 'Annecy, Léman, Alpes, Grands Lacs',
    sub: [{ id: 'lac_apercu', label: 'Aperçu / Proximité lac', coef: 5 }, { id: 'lac_directe', label: 'Belle vue directe', coef: 15 }, { id: 'lac_panoramique', label: 'Vue panoramique', coef: 30 }] },
  { id: 'montagne', label: 'Vue Montagne / Nature', icon: '🏔️', subtitle: 'Alpes, Pyrénées, Massif Central',
    sub: [{ id: 'montagne_vallee', label: 'Dégagée / Vallée / Campagne', coef: 5 }, { id: 'montagne_sommet', label: 'Imprenable / Sommets / Ski', coef: 15 }] },
  { id: 'degage', label: 'Vue Dégagée', icon: '🏙️', subtitle: 'Urbain — sans vis-à-vis',
    sub: [{ id: 'degage_parc', label: 'Dernier étage / Vue parc', coef: 5 }, { id: 'degage_standard', label: 'Standard (vis-à-vis classique)', coef: 0 }] },
];

const PROPERTY_FEATURES = {
  appartement: [
    { id: 'balcon', label: 'Balcon', coef: 3, icon: '🌿', inputType: 'surface' },
    { id: 'terrasse', label: 'Terrasse', coef: 5, icon: '☀️' },
    { id: 'parking', label: 'Parking', coef: 5, icon: '🅿️', inputType: 'quantity' },
    { id: 'garage', label: 'Garage', coef: 5, icon: '🚗', inputType: 'quantity' },
    { id: 'cave', label: 'Cave', coef: 2, icon: '🔑', inputType: 'quantity' },
    { id: 'ascenseur', label: 'Ascenseur', coef: 3, icon: '🛗' },
    { id: 'vue_type', label: 'Type de Vue', icon: '🏔️', inputType: 'vue_type' },
    { id: 'dernier_etage', label: 'Dernier étage', coef: 7, icon: '🔝' },
    { id: 'rdc', label: 'Rez-de-chaussée', coef: -5, icon: '⬇️' },
    { id: 'renove', label: 'Rénové / Neuf', coef: 10, icon: '✨' },
    { id: 'a_renover', label: 'À rénover', coef: -15, icon: '🔧' },
    { id: 'lumineux', label: 'Lumineux / Double expo.', coef: 4, icon: '💡' },
    { id: 'gardien', label: 'Gardien / Digicode', coef: 2, icon: '🔐' },
  ],
  maison: [
    { id: 'piscine', label: 'Piscine', coef: 8, icon: '🏊' },
    { id: 'jardin', label: 'Jardin', coef: 5, icon: '🌳' },
    { id: 'garage', label: 'Garage', coef: 4, icon: '🚗', inputType: 'quantity' },
    { id: 'parking', label: 'Parking', coef: 3, icon: '🅿️', inputType: 'quantity' },
    { id: 'cave', label: 'Cave', coef: 2, icon: '🔑', inputType: 'quantity' },
    { id: 'terrasse', label: 'Terrasse', coef: 4, icon: '☀️' },
    { id: 'balcon', label: 'Balcon', coef: 3, icon: '🌿', inputType: 'surface' },
    { id: 'sous_sol', label: 'Sous-sol aménagé', coef: 5, icon: '🏠' },
    { id: 'dependance', label: 'Dépendance / Annexe', coef: 6, icon: '🏡' },
    { id: 'vue_type', label: 'Type de Vue', icon: '🏔️', inputType: 'vue_type' },
    { id: 'renove', label: 'Rénové / Neuf', coef: 10, icon: '✨' },
    { id: 'a_renover', label: 'À rénover', coef: -15, icon: '🔧' },
    { id: 'calme', label: 'Quartier calme', coef: 3, icon: '🤫' },
    { id: 'panneau_solaire', label: 'Panneaux solaires', coef: 4, icon: '⚡' },
    { id: 'isolation', label: 'Bonne isolation (DPE A-C)', coef: 5, icon: '🌡️' },
  ],
  immeuble: [
    { id: 'commerce_rdc', label: 'Commerce au RDC', coef: 8, icon: '🏪' },
    { id: 'parking', label: 'Parking privatif', coef: 5, icon: '🚗', inputType: 'quantity' },
    { id: 'vue_type', label: 'Type de Vue', icon: '🏔️', inputType: 'vue_type' },
    { id: 'renove', label: 'Rénové / Bon état', coef: 10, icon: '✨' },
    { id: 'a_renover', label: 'À rénover', coef: -15, icon: '🔧' },
    { id: 'toiture_neuve', label: 'Toiture neuve', coef: 5, icon: '🏗️' },
    { id: 'plein_centre', label: 'Centre-ville', coef: 8, icon: '📍' },
    { id: 'tous_loues', label: 'Tous les lots loués', coef: 5, icon: '📋' },
  ],
  terrain: [
    { id: 'viabilise', label: 'Viabilisé', coef: 15, icon: '⚡' },
    { id: 'plat', label: 'Terrain plat', coef: 5, icon: '📐' },
    { id: 'vue_type', label: 'Type de Vue', icon: '🏔️', inputType: 'vue_type' },
    { id: 'acces_route', label: 'Accès route', coef: 5, icon: '🛣️' },
    { id: 'cu_positif', label: 'CU positif', coef: 10, icon: '📋' },
  ],
  local_commercial: [
    { id: 'vitrine', label: 'Vitrine sur rue', coef: 10, icon: '🪟' },
    { id: 'emplacement', label: 'Emplacement N°1', coef: 15, icon: '📍' },
    { id: 'extraction', label: 'Extraction / Hotte', coef: 5, icon: '💨' },
    { id: 'reserve', label: 'Réserve / Stockage', coef: 3, icon: '📦' },
    { id: 'renove', label: 'Rénové', coef: 8, icon: '✨' },
  ],
  bureau: [
    { id: 'climatisation', label: 'Climatisation', coef: 5, icon: '❄️' },
    { id: 'fibre', label: 'Fibre optique', coef: 3, icon: '🌐' },
    { id: 'parking', label: 'Parking', coef: 5, icon: '🚗', inputType: 'quantity' },
    { id: 'acces_handicape', label: 'Accès handicapé', coef: 3, icon: '♿' },
  ],
  entrepot: [
    { id: 'quai', label: 'Quai de déchargement', coef: 8, icon: '🚛' },
    { id: 'hauteur', label: 'Grande hauteur', coef: 5, icon: '📏' },
    { id: 'acces_pl', label: 'Accès poids lourd', coef: 5, icon: '🚚' },
  ],
  parking: [
    { id: 'securise', label: 'Sécurisé / Fermé', coef: 10, icon: '🔒' },
    { id: 'borne_elec', label: 'Borne électrique', coef: 8, icon: '⚡' },
    { id: 'proche_centre', label: 'Proche centre-ville', coef: 10, icon: '📍' },
  ],
};

const BUDGET_OPTIONS = [
  { value: 'moins_20k', label: 'Moins de 20 000 €' },
  { value: '20k_50k', label: '20 000 € - 50 000 €' },
  { value: '50k_100k', label: '50 000 € - 100 000 €' },
  { value: 'plus_100k', label: 'Plus de 100 000 €' },
];

export default function PartnerPage() {
  const { subdomain, commercial_slug } = useParams();
  const navigate = useNavigate();
  const [partner, setPartner] = useState(null);
  const [commercial, setCommercial] = useState(null); // données du commercial si page commercial
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeService, setActiveService] = useState(null);
  // Vitrine de biens
  const [vitrine, setVitrine] = useState([]);
  const [vitrineFilter, setVitrineFilter] = useState('all');
  const [selectedAnnonce, setSelectedAnnonce] = useState(null);
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [contactForm, setContactForm] = useState({ email: '', phone: '', message: '' });
  const [contactLoading, setContactLoading] = useState(false);
  const [contactSent, setContactSent] = useState(false);
  // Langue (FR / AR / EN) — uniquement pour partenaires étrangers
  const [lang, setLang] = useState('fr');
  const t = (key) => TRANSLATIONS[lang]?.[key] ?? TRANSLATIONS['fr'][key] ?? key;
  const isRTL = lang === 'ar';

  // Estimation state
  const [estimationStep, setEstimationStep] = useState(1);
  const [propertyType, setPropertyType] = useState('appartement');
  const [propertySubType, setPropertySubType] = useState('');
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [address, setAddress] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [surface, setSurface] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [estimationLoading, setEstimationLoading] = useState(false);
  const [estimationResult, setEstimationResult] = useState(null);
  const [featureQuantities, setFeatureQuantities] = useState({});
  const [vueCategory, setVueCategory] = useState(null);
  const [vueSub, setVueSub] = useState(null);
  const [vueExpanded, setVueExpanded] = useState(false);
  const [vueOptions, setVueOptions] = useState(VUE_OPTIONS);

  useEffect(() => {
    if (!subdomain) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/public/vue-coefficients?subdomain=${subdomain}`)
      .then(res => {
        if (res.data && res.data.length) {
          const normalized = res.data.map(cat => ({
            id: cat.category_id || cat.id,
            label: cat.category_label || cat.label,
            icon: cat.category_icon || cat.icon,
            subtitle: cat.category_subtitle || cat.subtitle,
            sub: (cat.subs || cat.sub || []).map(s => ({ id: s.sub_id || s.id, label: s.sub_label || s.label, coef: s.coef }))
          }));
          setVueOptions(normalized);
        }
      })
      .catch(() => {});
  }, [subdomain]);
  const [landSurface, setLandSurface] = useState('');

  // VIP / Chantier state
  const [vipSubMode, setVipSubMode] = useState(null);
  const [chantierData, setChantierData] = useState({ address: '', budget: '', phone: '', email: '', description: '' });
  const [chantierSubmitted, setChantierSubmitted] = useState(false);
  const [conseilData, setConseilData] = useState({ email: '', phone: '', conseil_type: '', description: '' });
  const [conseilSubmitted, setConseilSubmitted] = useState(false);
  // Formulaire simplifié Conseil pour les pages partenaires
  const [partnerConseilForm, setPartnerConseilForm] = useState({ email: '', phone: '', name: '' });
  const [partnerConseilSubmitted, setPartnerConseilSubmitted] = useState(false);
  const [partnerConseilLoading, setPartnerConseilLoading] = useState(false);

  // RGPD — un état par formulaire
  const [rgpdAudit, setRgpdAudit] = useState(false);
  const [rgpdConseil, setRgpdConseil] = useState(false);
  const [rgpdChantier, setRgpdChantier] = useState(false);
  const [rgpdPartnerConseil, setRgpdPartnerConseil] = useState(false);
  const [rgpdEstimation, setRgpdEstimation] = useState(false);

  // Audit state
  const [auditData, setAuditData] = useState({ address: '', surface: '', email: '', phone: '', description: '' });
  const [auditSubmitted, setAuditSubmitted] = useState(false);

  useEffect(() => { rememberPartner(subdomain); fetchPartner(); }, [subdomain]);
  useEffect(() => { if (partner) applyPwaBranding(subdomain, partner); }, [subdomain, partner]);
  
  // Chargement dynamique de la police Google Fonts
  useEffect(() => {
    if (!partner?.font_choice) return;
    const fontMap = {
      playfair: 'Playfair+Display:wght@400;700',
      raleway: 'Raleway:wght@400;600;700',
      montserrat: 'Montserrat:wght@400;600;700',
      inter: 'Inter:wght@400;600;700',
    };
    const fontKey = fontMap[partner.font_choice] || fontMap.inter;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css2?family=${fontKey}&display=swap`;
    document.head.appendChild(link);
    document.body.style.fontFamily = `'${partner.font_choice.charAt(0).toUpperCase() + partner.font_choice.slice(1)}', sans-serif`;
    return () => { document.body.style.fontFamily = ''; };
  }, [partner?.font_choice]);

  const fetchPartner = async () => {
    try {
      const res = await axios.get(`${API}/partners/by-subdomain/${subdomain}`);
      setPartner(res.data);
      // Charger la vitrine si ce partenaire est étranger (ou s'il a des annonces)
      try {
        const vitRes = await axios.get(`${API}/public/vitrine/${subdomain}`);
        setVitrine(vitRes.data || []);
      } catch { setVitrine([]); }
      // Si page commercial — charger les infos du commercial
      if (commercial_slug) {
        try {
          const commRes = await axios.get(`${API}/public/commercial/${subdomain}/${commercial_slug}`);
          setCommercial(commRes.data);
        } catch { /* commercial introuvable — on affiche quand même la page partenaire */ }
      }
    } catch { setError(true); } finally { setLoading(false); }
  };

  const color = partner?.primary_color || '#3B82F6';
  const accentColor = partner?.secondary_color || '#F59E0B';
  const ctaColor = partner?.cta_color || '#10B981';
  const headerColor = partner?.header_color || color;
  // Couleur de texte sur fond coloré : définie par le partenaire ou calculée auto (YIQ)
  const headerTextColor = partner?.text_color || getContrastYIQ(headerColor);
  const trustBarTextColor = partner?.text_color || getContrastYIQ(color);
  // Couleur lisible sur fond blanc (assombrit si trop clair)
  const colorOnWhite = getReadableColorOnWhite(color);
  const accentOnWhite = getReadableColorOnWhite(accentColor);
  // Couleur de texte sur fond coloré (pour boutons avec bg=color)
  const colorContrast = getContrastYIQ(color);
  const ctaTextColor = getContrastYIQ(ctaColor);

  // Contact affiché : commercial en priorité si on est sur une page commerciale
  const displayPhone = commercial?.telephone || partner?.contact_phone || partner?.phone || '';
  const displayEmail = commercial?.contact_email || partner?.contact_email || partner?.email || '';
  const displayName = commercial ? commercial.full_name : (partner?.company_name || partner?.full_name || '');
  const displayFonction = commercial?.fonction || '';
  const isCommercialPage = !!commercial_slug && !!commercial;
  // commercial_id à injecter dans toutes les soumissions de prospects
  const captureCommercialId = commercial?.id || null;
  const DEFAULT_HERO = 'https://images.unsplash.com/photo-1505843795480-5cfb3c03f6ff?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85';
  const DEFAULT_ABOUT_IMG = 'https://images.pexels.com/photos/8730006/pexels-photo-8730006.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
  const DEFAULT_GALLERY_IMG = 'https://images.pexels.com/photos/18153132/pexels-photo-18153132.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

  const toggleFeature = (featureId) => {
    setSelectedFeatures(prev => {
      if (prev.includes(featureId)) {
        setFeatureQuantities(q => { const nq = { ...q }; delete nq[featureId]; return nq; });
        return prev.filter(f => f !== featureId);
      }
      return [...prev, featureId];
    });
  };

  const updateFeatureQuantity = (featureId, value) => {
    setFeatureQuantities(prev => ({ ...prev, [featureId]: Math.max(0, value) }));
  };

  const getTotalCoefficient = () => {
    const features = PROPERTY_FEATURES[propertyType] || [];
    const base = selectedFeatures.reduce((total, fId) => {
      const feature = features.find(f => f.id === fId);
      if (!feature) return total;
      const qty = feature.inputType === 'quantity' ? (featureQuantities[fId] || 1) : 1;
      return total + (feature.coef || 0) * qty;
    }, 0);
    return base + (vueSub?.coef || 0);
  };

  // Address is managed locally in AddressInput component (no re-render on each keystroke)
  // Only update parent state for form validation
  const [addressValidated, setAddressValidated] = useState(false);
  const handleAddressChange = useCallback((value) => {
    setAddress(value);
    setAddressValidated(false); // reset si l'utilisateur modifie le texte
  }, []);

  const selectAddress = (suggestion) => {
    setAddress(suggestion.description);
    setAddressValidated(true); // adresse validée via suggestion
    setShowSuggestions(false);
  };

  const handleServiceClick = (service) => {
    if (service.id === 'radar') {
      navigate(`/radar?partner=${subdomain}`);
      return;
    }
    if (service.id === 'audit') {
      // Redirige vers le formulaire d'audit complet (CollectorPage) avec branding partenaire conservé
      navigate(`/?partner=${subdomain}&mode=audit`);
      return;
    }
    setActiveService(service.id);
    // Reset states
    setEstimationStep(1); setEstimationResult(null); setSelectedFeatures([]);
    setFeatureQuantities({}); setLandSurface('');
    setVueCategory(null); setVueSub(null); setVueExpanded(false);
    setVipSubMode(null); setChantierSubmitted(false); setConseilSubmitted(false);
    setAuditSubmitted(false);
  };

  const goBackToServices = () => {
    setActiveService(null);
  };

  const getEstimation = async () => {
    if (!email || !address || !surface) { toast.error('Veuillez remplir tous les champs'); return; }
    setEstimationLoading(true);
    try {
      const response = await axios.post(`${API}/estimation/dvf`, {
        email, phone: phone || undefined, address, surface: parseFloat(surface),
        property_type: propertyType, property_sub_type: propertySubType || undefined,
        features: selectedFeatures, features_coefficient: getTotalCoefficient(),
        feature_quantities: featureQuantities,
        land_surface: landSurface ? parseFloat(landSurface) : undefined,
        partner_subdomain: subdomain,
        ...(captureCommercialId ? { commercial_id: captureCommercialId } : {}),
      });
      setEstimationResult(response.data);
      toast.success('Estimation calculée !');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'estimation");
    } finally { setEstimationLoading(false); }
  };

  const submitChantier = async () => {
    if (!chantierData.address || !chantierData.budget || !chantierData.phone || !chantierData.email) {
      toast.error('Veuillez remplir les champs obligatoires'); return;
    }
    try {
      await axios.post(`${API}/chantier/request`, { ...chantierData, partner_subdomain: subdomain, ...(captureCommercialId ? { commercial_id: captureCommercialId } : {}) });
      setChantierSubmitted(true);
      toast.success('Demande envoyée !');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  // Soumission du formulaire conseil simplifié (partenaire uniquement)
  const submitPartnerConseil = async () => {
    if (!partnerConseilForm.email || !partnerConseilForm.email.includes('@')) {
      toast.error('Veuillez entrer un email valide'); return;
    }
    if (!partnerConseilForm.phone || partnerConseilForm.phone.trim().length < 8) {
      toast.error('Veuillez entrer un numéro de téléphone valide'); return;
    }
    setPartnerConseilLoading(true);
    try {
      await axios.post(`${API}/partner/conseil-contact`, {
        ...partnerConseilForm,
        partner_subdomain: subdomain,
        ...(captureCommercialId ? { commercial_id: captureCommercialId } : {}),
      });
      setPartnerConseilSubmitted(true);
    } catch {
      toast.error('Une erreur est survenue, veuillez réessayer.');
    } finally {
      setPartnerConseilLoading(false);
    }
  };

  const submitConseil = async () => {
    if (!conseilData.email || !conseilData.phone || !conseilData.conseil_type || !conseilData.description) {
      toast.error('Veuillez remplir tous les champs'); return;
    }
    try {
      await axios.post(`${API}/conseil/request`, { ...conseilData, partner_subdomain: subdomain, ...(captureCommercialId ? { commercial_id: captureCommercialId } : {}) });
      setConseilSubmitted(true);
      toast.success('Demande envoyée !');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const submitAudit = async () => {
    if (!auditData.address || !auditData.email || !auditData.phone) {
      toast.error('Veuillez remplir les champs obligatoires'); return;
    }
    try {
      await axios.post(`${API}/auth/magic-link`, {
        email: auditData.email, partner_subdomain: subdomain,
        project_data: { address: auditData.address, surface: auditData.surface, description: auditData.description, client_phone: auditData.phone },
        ...(captureCommercialId ? { commercial_id: captureCommercialId } : {}),
      });
      setAuditSubmitted(true);
      toast.success('Dossier envoyé !');
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Card className="max-w-md w-full mx-4">
        <CardContent className="p-8 text-center">
          <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-bold mb-2">Partenaire introuvable</h2>
          <p className="text-gray-500 mb-4">{"L'espace « " + subdomain + " » n'existe pas."}</p>
          <Button onClick={() => navigate('/')} variant="outline">Retour</Button>
        </CardContent>
      </Card>
    </div>
  );

  if (partner?.is_blocked) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50" data-testid="partner-blocked">
      <Card className="max-w-md w-full mx-4">
        <CardContent className="p-8 text-center">
          <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-bold mb-2">Espace temporairement indisponible</h2>
          <p className="text-gray-500 mb-4">{"Cet espace partenaire est actuellement suspendu. Veuillez contacter le partenaire pour plus d'informations."}</p>
          <Button onClick={() => navigate('/')} variant="outline">Retour</Button>
        </CardContent>
      </Card>
    </div>
  );

  // Pour les partenaires étrangers : forcer seulement estimation même si enabled_services est renseigné
  const isInternational = partner?.country && partner.country !== 'france';
  const rawEnabled = partner?.enabled_services || (isInternational ? ['estimation'] : ['estimation', 'radar', 'audit', 'conseil']);
  const enabledServices = isInternational ? rawEnabled.filter(s => s === 'estimation') : rawEnabled;
  const filteredServices = SERVICES_DEF.filter(s => enabledServices.includes(s.id));
  const displayStats = (partner?.custom_stats?.length ? partner.custom_stats : STATS_DEF.map(s => ({ ...s, label: t(s.labelKey) })));

  // AddressInput is now imported from ../components/AddressInput (with debounce + local state)

  // SUCCESS SCREEN
  const SuccessScreen = ({ title, message }) => (
    <div className="text-center py-12">
      <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: color + '20' }}>
        <CheckCircle className="w-10 h-10" style={{ color }} />
      </div>
      <h2 className="text-2xl font-bold text-gray-900 mb-2">{title}</h2>
      <p className="text-gray-500 mb-6">{message}</p>
      <Button onClick={goBackToServices} variant="outline">Retour aux services</Button>
    </div>
  );

  return (
    <div
      className="min-h-screen bg-white"
      data-testid="partner-page"
      dir={isRTL ? 'rtl' : 'ltr'}
      style={{
        fontFamily: isRTL
          ? "'Cairo', 'Noto Sans Arabic', sans-serif"
          : (partner?.font_choice && partner.font_choice !== 'inter' ? `'${partner.font_choice.charAt(0).toUpperCase() + partner.font_choice.slice(1)}'` : undefined)
      }}
    >
      {/* Font Arabic */}
      {isRTL && (
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;800&display=swap" />
      )}

      {/* ========== HEADER ========== */}
      <header className="sticky top-0 z-50 py-3 px-4 sm:px-8 shadow-md" style={{ backgroundColor: headerColor }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={goBackToServices}>
            {partner?.logo_url ? (
              <img src={partner.logo_url} alt={partner.name} className="h-10 sm:h-12 w-auto max-w-[160px] object-contain bg-white rounded-lg p-1 shrink-0" />
            ) : (
              <div className="h-10 w-10 bg-white/20 rounded-lg flex items-center justify-center shrink-0">
                <Building2 className="w-6 h-6" style={{ color: headerTextColor }} />
              </div>
            )}
            <div className="min-w-0 hidden sm:block">
              <h1 className="text-lg font-bold tracking-tight leading-tight truncate"
                style={{ color: headerTextColor }}>{partner?.name}</h1>
              {isCommercialPage ? (
                <p className="text-xs truncate font-medium" style={{ color: headerTextColor, opacity: 0.9 }}>
                  {displayName}{displayFonction ? ` — ${displayFonction}` : ''}
                </p>
              ) : partner?.slogan && (
                <p className="text-xs truncate"
                  style={{ color: headerTextColor, opacity: 0.85 }}>{partner.slogan}</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {displayPhone && (
              <a href={`tel:${displayPhone}`} className="hidden sm:flex items-center gap-2 text-sm font-medium transition-colors hover:opacity-80"
                style={{ color: headerTextColor }}>
                <Phone className="w-4 h-4" />
                {displayPhone}
              </a>
            )}
            {partner?.website_url && (
              <a href={partner.website_url} target="_blank" rel="noopener noreferrer" className="hidden md:flex items-center gap-1.5 text-sm font-medium transition-colors hover:opacity-80"
                style={{ color: headerTextColor, opacity: 0.85 }}>
                <Globe className="w-4 h-4" />
                <span>{partner.website_url.replace(/^https?:\/\/(www\.)?/, '')}</span>
              </a>
            )}
            {activeService && (
              <button onClick={goBackToServices} className="flex items-center gap-1 text-sm hover:opacity-80" style={{ color: headerTextColor }} data-testid="back-to-services">
                <ArrowLeft className="w-4 h-4" /> <span className="hidden sm:inline">Retour</span>
              </button>
            )}
            {!activeService && <InstallPwaButton appName={partner?.name} color={headerTextColor === '#ffffff' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.12)'} textColor={headerTextColor} />}
            {/* Toggle langue — uniquement partenaires étrangers */}
            {partner?.country && partner.country !== 'france' && (
              <div className="flex items-center gap-1 bg-black/10 rounded-full px-1 py-1">
                {LANG_OPTIONS.map(l => (
                  <button key={l.key} onClick={() => setLang(l.key)}
                    className={`px-2 py-0.5 rounded-full text-xs font-bold transition-all ${lang === l.key ? 'bg-white shadow' : 'hover:bg-white/20'}`}
                    style={{ color: lang === l.key ? colorOnWhite : headerTextColor }}
                    data-testid={`lang-toggle-${l.key}`}
                  >{l.label}</button>
                ))}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* ========== LANDING ========== */}
      {!activeService && (
        <div className="w-full">

          {/* HERO */}
          <section className="relative min-h-[480px] sm:min-h-[560px] flex items-center overflow-hidden"
            style={partner?.hero_image_url
              ? { backgroundImage: `url(${partner.hero_image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }
              : { backgroundImage: `url(${DEFAULT_HERO})`, backgroundSize: 'cover', backgroundPosition: 'center' }
            }>
            {/* Overlay coloré */}
            <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${color}D0 0%, ${color}90 50%, ${color}60 100%)` }} />
            {/* Couche sombre garantissant la lisibilité quel que soit la couleur */}
            <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(0,0,0,0.32) 0%, rgba(0,0,0,0.10) 70%, rgba(0,0,0,0.04) 100%)' }} />
            <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-8 py-16 sm:py-24 w-full">
              <div className="max-w-3xl">
                {partner?.logo_url && (
                  <div className="inline-block bg-white rounded-2xl p-3 mb-6 shadow-lg">
                    <img src={partner.logo_url} alt={partner.name} className="h-12 sm:h-16 w-auto max-w-[200px] object-contain" />
                  </div>
                )}
                <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight"
                  style={{ textShadow: '0 1px 4px rgba(0,0,0,0.55), 0 0 2px rgba(0,0,0,0.35)' }}>
                  {partner?.slogan || `${partner?.name}`}
                </h2>
                {partner?.slogan && (
                  <p className="text-xl sm:text-2xl text-white mb-2 font-light"
                    style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>
                    {partner?.name}
                  </p>
                )}
                <p className="text-white text-base sm:text-lg mb-8 max-w-xl leading-relaxed"
                  style={{ textShadow: '0 1px 3px rgba(0,0,0,0.5)' }}>
                  {t('hero_sub')}                </p>
                <div className="flex flex-wrap gap-3">
                  <button onClick={() => handleServiceClick(filteredServices[0])}
                    className="px-7 py-3.5 rounded-full text-sm sm:text-base font-semibold shadow-xl transition-all hover:shadow-2xl hover:-translate-y-0.5 bg-white"
                    style={{ color: colorOnWhite }} data-testid="partner-cta-estimation">
                    {t('cta_start')} →
                  </button>
                  {displayPhone ? (
                    <a href={`tel:${displayPhone}`}
                      className="flex items-center gap-2 px-6 py-3.5 rounded-full text-sm font-medium text-white border border-white/40 hover:bg-white/10 transition-all">
                      <Phone className="w-4 h-4" /> {t('call_us')}
                    </a>
                  ) : null}
                </div>
              </div>
            </div>
          </section>

          {/* BANDEAU DE CONFIANCE */}
          <section className="py-4 px-4 sm:px-8 border-y" style={{ backgroundColor: color }}>
            <div className="max-w-6xl mx-auto flex flex-wrap justify-center sm:justify-between items-center gap-4 sm:gap-0">
              {[
                { icon: Shield, label: t('certified') },
                { icon: CheckCircle, label: t('results') },
                { icon: MapPin, label: t('experts') },
                { icon: Star, label: t('satisfaction') },
              ].map(({ icon: Icon, label }, i) => (
                <div key={i} className="flex items-center gap-2"
                  style={{ color: trustBarTextColor }}>
                  <Icon className="w-4 h-4" style={{ color: trustBarTextColor }} />
                  <span className="text-sm font-semibold">{label}</span>
                  {i < 3 && <span className="hidden sm:block w-px h-4 ml-4" style={{ backgroundColor: trustBarTextColor, opacity: 0.25 }} />}
                </div>
              ))}
            </div>
          </section>

          {/* STATS */}
          <section className="py-6 px-4 sm:px-8 border-b" style={{ backgroundColor: color + '08' }}>
            <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
              {displayStats.map((stat, i) => (
                <div key={i} className="text-center py-4">
                  <p className="text-2xl sm:text-3xl font-bold" dir="ltr" style={{ color: colorOnWhite }}>{stat.value}</p>
                  <p className="text-xs sm:text-sm text-gray-500 mt-0.5">{stat.label}</p>
                </div>
              ))}
            </div>
          </section>

          <main className="max-w-6xl mx-auto px-4 sm:px-8 py-12 sm:py-16 space-y-16">

            {/* SERVICES */}
            <section>
              <div className="text-center mb-8">
                <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-3" style={{ backgroundColor: accentColor + '20', color: accentOnWhite }}>{t('services_label')}</span>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">{t('services_title')}</h2>
              </div>
              <div className="grid md:grid-cols-2 gap-5">
                {filteredServices.map((service) => {
                  const Icon = service.icon;
                  return (
                    <button key={service.id} onClick={() => handleServiceClick(service)}
                      className="group p-6 sm:p-8 rounded-2xl border-2 text-left transition-all duration-300 hover:shadow-xl hover:-translate-y-1 bg-white"
                      style={{ borderColor: '#e5e7eb' }}
                      onMouseEnter={(e) => { e.currentTarget.style.borderColor = color; }}
                      onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; }}
                      data-testid={`service-${service.id}`}>
                      <div className="flex items-start gap-5">
                        <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${service.iconBg} group-hover:scale-110 transition-transform`}>
                          <Icon className={`w-7 h-7 ${service.iconColor}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="text-lg font-bold text-gray-900">{t(service.labelKey)}</span>
                            <Badge className={`text-[10px] ${service.badgeColor}`}>{t(service.badgeKey)}</Badge>
                          </div>
                          <p className="text-sm text-gray-500 leading-relaxed mb-4">{t(service.descKey)}</p>
                          <div className="flex items-center gap-1.5 text-sm font-semibold transition-colors" style={{ color: colorOnWhite }}>
                            <span>{lang === 'ar' ? 'الوصول' : lang === 'en' ? 'Access' : 'Accéder'}</span>
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1.5 transition-transform" />
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* ===== VITRINE DE BIENS ===== */}
            {vitrine.length > 0 && (
              <section id="vitrine" className="scroll-mt-20">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
                  <div>
                    <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-3" style={{ backgroundColor: accentColor + '20', color: accentOnWhite }}>{t('our_biens')}</span>
                    <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">{t('vitrine_title')}</h2>
                    <p className="text-gray-500 mt-2 text-sm">{(() => { const n = vitrine.filter(a => vitrineFilter === 'all' || a.transaction_type === vitrineFilter).length; return `${n} ${n > 1 ? t('biens_plural') : t('biens_singular')}`; })()}</p>
                  </div>
                  {/* Filtres */}
                  <div className="flex gap-2">
                    {[{k:'all',l:t('vitrine_all')},{k:'vente',l:t('vitrine_sale')},{k:'location',l:t('vitrine_rent')}].map(f => (
                      <button key={f.k} onClick={() => setVitrineFilter(f.k)}
                        className={`px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${vitrineFilter === f.k ? 'text-white shadow-lg scale-105' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                        style={vitrineFilter === f.k ? { backgroundColor: color } : {}}>
                        {f.l}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Grille d'annonces */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {vitrine.filter(a => vitrineFilter === 'all' || a.transaction_type === vitrineFilter).map(ann => {
                    const currency = ann.country === 'algerie' ? 'DZD' : ann.country === 'maroc' ? 'MAD' : ann.country === 'tunisie' ? 'TND' : '';
                    return (
                      <div key={ann.id}
                        onClick={() => { setSelectedAnnonce(ann); setCarouselIndex(0); setContactSent(false); setContactForm({ email:'', phone:'', message:'' }); }}
                        className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-2xl hover:-translate-y-1.5 transition-all duration-400 cursor-pointer border border-gray-100"
                        data-testid={`public-annonce-${ann.id}`}>

                        {/* Photo principale */}
                        <div className="relative h-56 overflow-hidden bg-gray-100">
                          {ann.photos?.length > 0 ? (
                            <>
                              <img src={ann.photos[0]} alt={ann.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                            </>
                          ) : (
                            <>
                              <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${color}25, ${color}50)` }} />
                              <Home className="absolute inset-0 m-auto w-16 h-16 opacity-30" style={{ color }} />
                            </>
                          )}
                          {/* Badges overlay */}
                          <div className="absolute top-3 left-3 flex gap-2">
                            <span className={`px-3 py-1 rounded-full text-xs font-bold text-white backdrop-blur-sm ${ann.transaction_type === 'vente' ? 'bg-blue-600/90' : 'bg-purple-600/90'}`}>
                              {ann.transaction_type === 'vente' ? t('vitrine_sale') : t('vitrine_rent')}
                            </span>
                          </div>
                          {ann.photos?.length > 1 && (
                            <span className="absolute top-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm flex items-center gap-1">
                              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd"/></svg>
                              {ann.photos.length}
                            </span>
                          )}
                          {/* Prix sur la photo */}
                          {ann.photos?.length > 0 && (
                            <div className="absolute bottom-3 left-3 right-3">
                              <p className="text-white font-bold text-xl drop-shadow-lg">
                                {parseInt(ann.price).toLocaleString('fr-FR')} <span className="text-sm font-semibold">{currency}</span>
                                {ann.transaction_type === 'location' && <span className="text-sm font-normal opacity-80">/mois</span>}
                              </p>
                            </div>
                          )}
                        </div>

                        {/* Contenu carte */}
                        <div className="p-5">
                          <h3 className="font-bold text-gray-900 text-base leading-snug mb-2 line-clamp-2 group-hover:opacity-80 transition-opacity">{ann.title}</h3>
                          <p className="text-xs text-gray-500 mb-3 flex items-center gap-1.5">
                            <MapPin className="w-3.5 h-3.5 flex-shrink-0" style={{ color }} />
                            {ann.zone_label || ann.city || '—'}
                          </p>

                          {/* Stats */}
                          <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                            {ann.surface && (
                              <span className="flex items-center gap-1 text-xs text-gray-600 bg-gray-50 rounded-lg px-2.5 py-1.5">
                                <Home className="w-3 h-3" /> {ann.surface} m²
                              </span>
                            )}
                            {ann.rooms && (
                              <span className="flex items-center gap-1 text-xs text-gray-600 bg-gray-50 rounded-lg px-2.5 py-1.5">
                                <Users className="w-3 h-3" /> {ann.rooms} p.
                              </span>
                            )}
                            {!ann.photos?.length && (
                              <span className="text-lg font-bold ml-auto" style={{ color: colorOnWhite }}>
                                {parseInt(ann.price).toLocaleString('fr-FR')} <span className="text-xs font-semibold">{currency}</span>
                              </span>
                            )}
                            <span className="ms-auto text-xs font-semibold flex items-center gap-1 transition-all group-hover:gap-2" style={{ color }}>
                              {t('voir')} <ArrowRight className="w-3.5 h-3.5" />
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* ===== MODAL ANNONCE + CAROUSEL ===== */}
            {selectedAnnonce && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4" onClick={() => setSelectedAnnonce(null)}>
                <div className="absolute inset-0 bg-black/60 backdrop-blur-md" />
                <div className="relative bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[95vh] overflow-y-auto" onClick={e => e.stopPropagation()}>

                  {/* ===== CAROUSEL PHOTOS ===== */}
                  {selectedAnnonce.photos?.length > 0 ? (
                    <div className="relative">
                      {/* Photo principale */}
                      <div className="h-64 sm:h-80 overflow-hidden rounded-t-3xl relative bg-gray-900">
                        <img
                          key={carouselIndex}
                          src={selectedAnnonce.photos[carouselIndex]}
                          alt={`${selectedAnnonce.title} ${carouselIndex + 1}`}
                          className="w-full h-full object-cover animate-fadeIn"
                          style={{ animation: 'fadeIn 0.3s ease' }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />

                        {/* Compteur */}
                        <span className="absolute top-4 right-4 bg-black/50 text-white text-xs px-3 py-1.5 rounded-full backdrop-blur-sm font-medium">
                          {carouselIndex + 1} / {selectedAnnonce.photos.length}
                        </span>

                        {/* Flèches */}
                        {selectedAnnonce.photos.length > 1 && (
                          <>
                            <button onClick={e => { e.stopPropagation(); setCarouselIndex(i => (i - 1 + selectedAnnonce.photos.length) % selectedAnnonce.photos.length); }}
                              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all hover:scale-110">
                              <ArrowLeft className="w-5 h-5" />
                            </button>
                            <button onClick={e => { e.stopPropagation(); setCarouselIndex(i => (i + 1) % selectedAnnonce.photos.length); }}
                              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/20 hover:bg-white/40 backdrop-blur-sm rounded-full flex items-center justify-center text-white transition-all hover:scale-110">
                              <ArrowRight className="w-5 h-5" />
                            </button>
                          </>
                        )}

                        {/* Dots */}
                        {selectedAnnonce.photos.length > 1 && (
                          <div className="absolute bottom-4 left-0 right-0 flex justify-center gap-1.5">
                            {selectedAnnonce.photos.map((_, i) => (
                              <button key={i} onClick={e => { e.stopPropagation(); setCarouselIndex(i); }}
                                className={`rounded-full transition-all duration-300 ${i === carouselIndex ? 'w-6 h-2 bg-white' : 'w-2 h-2 bg-white/50 hover:bg-white/75'}`} />
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Miniatures */}
                      {selectedAnnonce.photos.length > 1 && (
                        <div className="flex gap-2 px-4 pt-3 pb-0 overflow-x-auto scrollbar-hide">
                          {selectedAnnonce.photos.map((photo, i) => (
                            <button key={i} onClick={() => setCarouselIndex(i)}
                              className={`flex-shrink-0 w-16 h-12 rounded-xl overflow-hidden border-2 transition-all ${i === carouselIndex ? 'border-2 scale-105 shadow-md' : 'border-transparent opacity-60 hover:opacity-100'}`}
                              style={i === carouselIndex ? { borderColor: color } : {}}>
                              <img src={photo} alt={`thumb-${i}`} className="w-full h-full object-cover" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="h-48 flex items-center justify-center rounded-t-3xl" style={{ background: `linear-gradient(135deg, ${color}20, ${color}40)` }}>
                      <Home className="w-16 h-16 opacity-30" style={{ color }} />
                    </div>
                  )}

                  {/* Contenu modal */}
                  <div className="p-6">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1 pr-4">
                        <div className="flex gap-2 flex-wrap mb-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${selectedAnnonce.transaction_type === 'vente' ? 'bg-blue-600' : 'bg-purple-600'}`}>
                            {selectedAnnonce.transaction_type === 'vente' ? t('vitrine_sale') : t('vitrine_rent')}
                          </span>
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            {selectedAnnonce.property_type?.replace('_',' ')}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 leading-tight">{selectedAnnonce.title}</h3>
                        <p className="text-gray-500 text-sm mt-1.5 flex items-center gap-1.5">
                          <MapPin className="w-4 h-4 flex-shrink-0" style={{ color }} />
                          {selectedAnnonce.zone_label || selectedAnnonce.city || '—'}
                        </p>
                      </div>
                      <button onClick={() => setSelectedAnnonce(null)}
                        className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 transition-colors flex-shrink-0">
                        <X className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Prix */}
                    <div className="p-4 rounded-2xl mb-5" style={{ backgroundColor: color + '12' }}>
                      <p className="text-sm text-gray-500 mb-1">{selectedAnnonce.transaction_type === 'location' ? 'Loyer mensuel' : 'Prix de vente'}</p>
                      <p className="text-3xl font-bold" style={{ color }}>
                        {parseInt(selectedAnnonce.price).toLocaleString('fr-FR')}
                        <span className="text-lg font-semibold ml-2">{selectedAnnonce.country === 'algerie' ? 'DZD' : selectedAnnonce.country === 'maroc' ? 'MAD' : selectedAnnonce.country === 'tunisie' ? 'TND' : ''}</span>
                        {selectedAnnonce.transaction_type === 'location' && <span className="text-base font-normal text-gray-500 ml-1">/mois</span>}
                      </p>
                    </div>

                    {/* Caractéristiques */}
                    <div className="grid grid-cols-3 gap-3 mb-5">
                      {selectedAnnonce.surface && (
                        <div className="bg-gray-50 rounded-xl p-3 text-center">
                          <p className="text-lg font-bold text-gray-900">{selectedAnnonce.surface}</p>
                          <p className="text-xs text-gray-500">m²</p>
                        </div>
                      )}
                      {selectedAnnonce.rooms && (
                        <div className="bg-gray-50 rounded-xl p-3 text-center">
                          <p className="text-lg font-bold text-gray-900">{selectedAnnonce.rooms}</p>
                          <p className="text-xs text-gray-500">pièces</p>
                        </div>
                      )}
                      {selectedAnnonce.surface && selectedAnnonce.price && (
                        <div className="bg-gray-50 rounded-xl p-3 text-center">
                          <p className="text-lg font-bold text-gray-900">{Math.round(selectedAnnonce.price / selectedAnnonce.surface).toLocaleString('fr-FR')}</p>
                          <p className="text-xs text-gray-500">{selectedAnnonce.country === 'algerie' ? 'DZD' : 'MAD'}/m²</p>
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    {selectedAnnonce.description && (
                      <p className="text-gray-600 text-sm leading-relaxed mb-5 whitespace-pre-line">{selectedAnnonce.description}</p>
                    )}

                    {/* Téléphone direct */}
                    {selectedAnnonce.contact_phone && (
                      <a href={`tel:${selectedAnnonce.contact_phone}`}
                        className="flex items-center justify-center gap-2 w-full py-3 rounded-2xl text-sm font-bold mb-4 transition-all hover:opacity-90"
                        style={{ backgroundColor: color + '15', color: colorOnWhite }}>
                        <Phone className="w-4 h-4" /> {selectedAnnonce.contact_phone}
                      </a>
                    )}

                    <hr className="mb-4" />

                    {/* Formulaire contact */}
                    {contactSent ? (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5 text-center">
                        <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                          <CheckCircle className="w-6 h-6 text-emerald-600" />
                        </div>
                        <p className="font-bold text-emerald-700">{t('request_sent')}</p>
                        <p className="text-sm text-emerald-600 mt-1">{t('request_sent_sub')}</p>
                      </div>
                    ) : (
                      <div>
                        <p className="font-bold text-gray-800 mb-3 text-base">{t('contact_annonce')}</p>
                        <div className="space-y-3">
                          <input type="email" value={contactForm.email} onChange={e => setContactForm(f => ({...f, email: e.target.value}))}
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 transition-shadow"
                            style={{ focusRingColor: color }} placeholder={t('email_placeholder')} />
                          <input type="tel" value={contactForm.phone} onChange={e => setContactForm(f => ({...f, phone: e.target.value}))}
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 transition-shadow"
                            placeholder={t('phone_placeholder')} />
                          <textarea value={contactForm.message} onChange={e => setContactForm(f => ({...f, message: e.target.value}))}
                            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 transition-shadow"
                            rows={3} placeholder={t('message_placeholder')} />
                          <button
                            onClick={async () => {
                              if (!contactForm.email) { alert(t('email_placeholder')); return; }
                              setContactLoading(true);
                              try {
                                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/public/vitrine/${selectedAnnonce.id}/contact`, contactForm);
                                setContactSent(true);
                              } catch { alert('Erreur lors de l\'envoi'); } finally { setContactLoading(false); }
                            }}
                            disabled={contactLoading || !contactForm.email}
                            className="w-full py-3.5 rounded-2xl font-bold text-sm transition-all hover:opacity-90 hover:-translate-y-0.5 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                            style={{ backgroundColor: color, color: colorContrast }}
                            data-testid="vitrine-contact-btn"
                          >
                            {contactLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('sending')}</> : <><Send className="w-4 h-4" /> {t('send_btn')}</>}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ABOUT */}
            {partner?.about_text && (
              <section className="grid md:grid-cols-2 gap-10 items-center">
                <div>
                  <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-4" style={{ backgroundColor: accentColor + '20', color: accentOnWhite }}>{t('about_title')}</span>
                  <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">{t('about_title')}</h2>
                  <p className="text-gray-600 leading-relaxed text-base whitespace-pre-line">{partner.about_text}</p>
                  <div className="flex flex-wrap gap-3 mt-6">
                    {displayPhone && (
                      <a href={`tel:${displayPhone}`} className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold transition-all hover:-translate-y-0.5 shadow-md hover:shadow-lg" style={{ backgroundColor: color, color: colorContrast }}>
                        <Phone className="w-4 h-4" /> {displayPhone}
                      </a>
                    )}
                    {partner.website_url && (
                      <a href={partner.website_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold border transition-all hover:-translate-y-0.5" style={{ color: colorOnWhite, borderColor: colorOnWhite + '60' }}>
                        <Globe className="w-4 h-4" /> {lang === 'ar' ? 'موقعنا' : lang === 'en' ? 'Our website' : 'Notre site'}
                      </a>
                    )}
                  </div>
                </div>
                <div className="rounded-3xl overflow-hidden aspect-video shadow-xl">
                  <img
                    src={partner.about_image_url || partner.hero_image_url || DEFAULT_ABOUT_IMG}
                    alt={partner.name}
                    className="w-full h-full object-cover"
                    onError={e => { e.target.src = DEFAULT_ABOUT_IMG; }}
                  />
                </div>
              </section>
            )}

            {/* WHY US */}
            {(() => {
              const defaultWhyUs = [
                { title: t('eng_expertise_title'), text: t('eng_expertise_desc'), icon_name: 'award' },
                { title: t('eng_rapide_title'), text: t('eng_rapide_desc'), icon_name: 'zap' },
                { title: t('eng_certif_title'), text: t('eng_certif_desc'), icon_name: 'shield' },
              ];
              const items = partner?.why_us?.length > 0 ? partner.why_us : defaultWhyUs;
              const iconMap = { award: Award, zap: Zap, shield: Shield, users: Users, star: Star, target: Target, trending: TrendingUp, home: Home };
              return (
                <section>
                  <div className="text-center mb-10">
                    <span className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest mb-3" style={{ backgroundColor: accentColor + '20', color: accentOnWhite }}>{t('why_us_label')}</span>
                    <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">{t('engagements_title')}</h2>
                  </div>
                  <div className="grid md:grid-cols-3 gap-6">
                    {items.map((item, i) => {
                      const Icon = iconMap[item.icon_name] || Award;
                      return (
                        <div key={i} className="p-8 rounded-3xl text-center transition-all hover:-translate-y-1 hover:shadow-lg" style={{ background: `linear-gradient(135deg, ${color}08, ${color}04)`, border: `1.5px solid ${color}20` }}>
                          <div className="w-14 h-14 rounded-2xl mx-auto mb-5 flex items-center justify-center" style={{ backgroundColor: color + '18' }}>
                            <Icon className="w-7 h-7" style={{ color: colorOnWhite }} />
                          </div>
                          <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                          <p className="text-sm text-gray-500 leading-relaxed">{item.text}</p>
                        </div>
                      );
                    })}
                  </div>
                </section>
              );
            })()}

            {/* GALERIE IMAGE */}
            {(partner?.gallery_image_url || true) && (
              <section className="rounded-3xl overflow-hidden shadow-xl h-64 sm:h-80 relative">
                <img
                  src={partner?.gallery_image_url || DEFAULT_GALLERY_IMG}
                  alt={`${partner?.name} - notre agence`}
                  className="w-full h-full object-cover"
                  onError={e => { e.target.src = DEFAULT_GALLERY_IMG; }}
                />
                <div className="absolute inset-0 flex items-end" style={{ background: `linear-gradient(to top, ${color}CC 0%, transparent 50%)` }}>
                  <div className="p-6 sm:p-8">
                    <p className="font-bold text-xl sm:text-2xl" style={{ color: colorContrast }}>{partner?.name}</p>
                    <p className="text-sm mt-1" style={{ color: colorContrast, opacity: 0.8 }}>{t('gallery_label')}</p>
                  </div>
                </div>
              </section>
            )}

            {/* CTA FINAL */}
            <section className="rounded-3xl p-10 sm:p-14 text-center relative overflow-hidden" style={{ background: `linear-gradient(135deg, ${ctaColor} 0%, ${ctaColor}CC 100%)` }}>
              <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
              <div className="relative z-10">
                <Zap className="w-10 h-10 mx-auto mb-4" style={{ color: ctaTextColor, opacity: 0.8 }} />
                <h2 className="text-3xl sm:text-4xl font-bold mb-3" style={{ color: ctaTextColor }}>{t('cta_title')}</h2>
                <p className="text-base mb-8 max-w-lg mx-auto" style={{ color: ctaTextColor, opacity: 0.8 }}>
                  {t('cta_sub')}
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                  <button onClick={() => handleServiceClick(filteredServices[0])}
                    className="px-8 py-3.5 rounded-full text-sm sm:text-base font-semibold bg-white transition-all hover:-translate-y-0.5 hover:shadow-xl"
                    style={{ color: colorOnWhite }} data-testid="partner-cta-final">
                    {t('cta_btn')} →
                  </button>
                  {displayPhone && (
                    <a href={`tel:${displayPhone}`}
                      className="flex items-center gap-2 px-7 py-3.5 rounded-full transition-all text-sm font-medium hover:opacity-80"
                      style={{ color: ctaTextColor, border: `1px solid ${ctaTextColor}60`, backgroundColor: `${ctaTextColor}15` }}>
                      <Phone className="w-4 h-4" /> {displayPhone}
                    </a>
                  )}
                </div>
              </div>
            </section>

          </main>

          {/* FOOTER */}
          <footer className="border-t py-8 px-4 sm:px-8 text-center" style={{ backgroundColor: color + '06' }}>
            <p className="text-sm font-semibold text-gray-700 mb-2">{isCommercialPage ? displayName : (partner?.name || partner?.company_name)}</p>
            {isCommercialPage && displayFonction && (
              <p className="text-xs text-gray-500 mb-1">{displayFonction}</p>
            )}
            {displayEmail && (
              <a href={`mailto:${displayEmail}`} className="text-sm hover:underline inline-flex items-center gap-1.5" style={{ color: colorOnWhite }}>
                <Mail className="w-3.5 h-3.5" />{displayEmail}
              </a>
            )}
            {isCommercialPage && displayPhone && (
              <div className="mt-1">
                <a href={`tel:${displayPhone}`} className="text-sm hover:underline inline-flex items-center gap-1.5" style={{ color: colorOnWhite }}>
                  <Phone className="w-3.5 h-3.5" />{displayPhone}
                </a>
              </div>
            )}
          </footer>
        </div>
      )}

      {/* ========== SERVICE FORMS ========== */}
      {activeService && (
        <main className="max-w-5xl mx-auto px-3 sm:px-4 py-5 sm:py-8">
          {activeService === 'estimation' && (
          <div className="max-w-2xl mx-auto">
            {/* Progress */}
            <div className="mb-6">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="font-medium text-gray-700">{"Étape " + estimationStep + " / 4"}</span>
                <span className="font-medium" style={{ color }}>{Math.round((estimationStep / 4) * 100)}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${(estimationStep / 4) * 100}%`, backgroundColor: color }} />
              </div>
            </div>

            {/* Step 1: Type + Adresse */}
            {estimationStep === 1 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: color + '15' }}>
                    <MapPin className="w-8 h-8" style={{ color }} />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Estimation gratuite</h2>
                  <p className="text-gray-500 mt-2">{"Obtenez le prix au m² réel basé sur les ventes des 3 dernières années"}</p>
                </div>

                <Card className="border-2 shadow-lg" style={{ borderColor: color + '40' }}>
                  <CardContent className="p-6">
                    <Label className="font-semibold text-base mb-4 block">Type de bien</Label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {PROPERTY_TYPES.map((type) => (
                        <button key={type.id} data-testid={`property-type-${type.id}`}
                          onClick={() => { setPropertyType(type.id); setPropertySubType(''); setSelectedFeatures([]); setFeatureQuantities({}); setVueCategory(null); setVueSub(null); setVueExpanded(false); }}
                          className={`p-3 rounded-xl border-2 transition-all text-center ${propertyType === type.id ? 'ring-2' : 'border-gray-200 hover:border-gray-300'}`}
                          style={propertyType === type.id ? { borderColor: color, backgroundColor: color + '10', boxShadow: `0 0 0 2px ${color}` } : {}}>
                          <span className="text-2xl block mb-1">{type.icon}</span>
                          <span className="text-xs font-medium">{type.label}</span>
                        </button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-2 shadow-lg" style={{ borderColor: color + '40' }}>
                  <CardContent className="p-6">
                    <Label className="font-semibold text-base mb-4 block">{"Adresse du bien à estimer"}</Label>
                    <AddressInput value={address} onChange={handleAddressChange} onSelect={selectAddress} />
                    {address.length >= 5 && !addressValidated && (
                      <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                        <span>⚠</span> Sélectionnez une adresse dans la liste pour continuer
                      </p>
                    )}
                  </CardContent>
                </Card>

                <div className="flex justify-end">
                  <Button onClick={() => setEstimationStep(2)} disabled={address.length < 5 || !addressValidated}
                    className="text-white px-8" style={{ backgroundColor: color }} data-testid="estimation-next-1">
                    Continuer <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 2: Email + Téléphone */}
            {estimationStep === 2 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: color + '15' }}>
                    <Mail className="w-8 h-8" style={{ color }} />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Vos coordonnées</h2>
                  <p className="text-gray-500 mt-2">{"Renseignez vos coordonnées pour recevoir votre estimation personnalisée"}</p>
                </div>

                <Card className="border-2 shadow-lg" style={{ borderColor: color + '40' }}>
                  <CardContent className="p-8">
                    <div className="flex items-center gap-2 mb-6 justify-center">
                      <Award className="w-5 h-5" style={{ color }} />
                      <p className="text-sm font-medium">{"Vous recevrez les conseils de l'Expert (25 ans d'expérience)"}</p>
                    </div>
                    <div className="max-w-md mx-auto space-y-4">
                      <div>
                        <Label className="font-semibold text-sm mb-2 block">Email *</Label>
                        <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                          placeholder="votre@email.com" className="text-lg py-6" data-testid="estimation-email-input" />
                      </div>
                      <div>
                        <Label className="font-semibold text-sm mb-2 block">{"Téléphone (optionnel)"}</Label>
                        <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
                          placeholder="06 12 34 56 78" className="text-lg py-6" data-testid="estimation-phone-input" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-400 mt-4 text-center">{"Vos données sont sécurisées et ne seront jamais partagées"}</p>
                  </CardContent>
                </Card>

                <div className="flex justify-between items-end">
                  <Button variant="outline" onClick={() => setEstimationStep(1)}>
                    <ArrowLeft className="w-4 h-4 mr-2" /> Retour
                  </Button>
                  <div className="text-right">
                    <RgpdCheckbox checked={rgpdEstimation} onChange={setRgpdEstimation} companyName={partner?.company_name || partner?.name} />
                    <Button onClick={async () => {
                      try { await axios.post(`${API}/prospect/capture`, { email, phone, address, property_type: propertyType, source: 'estimation', partner_subdomain: subdomain, ...(captureCommercialId ? { commercial_id: captureCommercialId } : {}) }); } catch (e) { console.error(e); }
                      setEstimationStep(3);
                    }} disabled={!email || !rgpdEstimation} className="text-white px-8 mt-2" style={{ backgroundColor: color }} data-testid="estimation-next-2">
                      Continuer <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Détails du bien */}
            {estimationStep === 3 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: color + '15' }}>
                    <Home className="w-8 h-8" style={{ color }} />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">{"Détails du bien"}</h2>
                  <p className="text-gray-500 mt-2">{address}</p>
                </div>

                {/* Sub-type */}
                {PROPERTY_SUBCATEGORIES[propertyType] && (
                  <Card className="border-2 shadow-lg" style={{ borderColor: color + '40' }}>
                    <CardContent className="p-6">
                      <Label className="font-semibold text-base mb-4 block">{"Précisez le type"}</Label>
                      <div className="flex flex-wrap gap-2">
                        {PROPERTY_SUBCATEGORIES[propertyType].map((sub) => (
                          <button key={sub.id} data-testid={`property-subtype-${sub.id}`}
                            onClick={() => setPropertySubType(sub.id)}
                            className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${propertySubType === sub.id ? 'ring-1' : 'border-gray-200 hover:border-gray-300'}`}
                            style={propertySubType === sub.id ? { borderColor: color, backgroundColor: color + '10', boxShadow: `0 0 0 1px ${color}` } : {}}>
                            {sub.label}
                          </button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Features */}
                {PROPERTY_FEATURES[propertyType] && (
                  <Card className="border-2 shadow-lg" style={{ borderColor: color + '40' }}>
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <Label className="font-semibold text-base">{"Caractéristiques du bien"}</Label>
                        {selectedFeatures.length > 0 && (
                          <Badge style={{ backgroundColor: color + '20', color }}>{selectedFeatures.length} {"sélectionnée" + (selectedFeatures.length > 1 ? 's' : '')}</Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {PROPERTY_FEATURES[propertyType].map((feature) => {
                          const isSelected = selectedFeatures.includes(feature.id);
                          const isQuantifiable = feature.inputType === 'quantity';
                          const isSurface = feature.inputType === 'surface';

                          if (feature.inputType === 'vue_type') {
                            const currentCat = vueOptions.find(c => c.id === vueCategory);
                            return (
                              <div key={feature.id} className="col-span-2 md:col-span-3 flex flex-col">
                                <button data-testid="feature-vue_type"
                                  onClick={() => setVueExpanded(!vueExpanded)}
                                  className={`flex items-center gap-2 px-3 py-2.5 border-2 text-sm transition-all text-left ${(vueExpanded || vueSub) ? 'rounded-t-lg rounded-b-none border-b-0' : 'rounded-lg'} ${vueSub ? '' : 'border-gray-200 hover:border-gray-300'}`}
                                  style={vueSub ? { borderColor: color, backgroundColor: color + '10' } : {}}>
                                  <span className="text-lg shrink-0">🏔️</span>
                                  <span className="flex-1 font-medium text-xs">
                                    {vueSub ? `Vue : ${vueSub.label}` : 'Type de Vue'}
                                  </span>
                                  {vueSub
                                    ? <button type="button" onClick={(e) => { e.stopPropagation(); setVueSub(null); setVueCategory(null); setVueExpanded(false); }} className="text-xs px-1.5 py-0.5 rounded hover:bg-red-100 text-red-500">✕</button>
                                    : <span className="text-xs text-gray-400">{vueExpanded ? '▲' : '▼'}</span>
                                  }
                                </button>
                                {(vueExpanded || vueSub) && (
                                  <div className="border-2 border-t-0 rounded-b-lg p-3 space-y-3"
                                    style={{ borderColor: vueSub ? color : '#E5E7EB', backgroundColor: vueSub ? color + '08' : '#F9FAFB' }}>
                                    <div>
                                      <p className="text-xs font-medium text-gray-500 mb-2">Catégorie</p>
                                      <div className="grid grid-cols-2 gap-1.5">
                                        {vueOptions.map(cat => (
                                          <button key={cat.id} type="button"
                                            onClick={() => { setVueCategory(cat.id); setVueSub(null); }}
                                            className={`flex flex-col items-start p-2 rounded-lg border text-xs transition-all ${vueCategory === cat.id ? '' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                                            style={vueCategory === cat.id ? { borderColor: color, backgroundColor: color + '15' } : {}}>
                                            <span className="font-medium">{cat.icon} {cat.label}</span>
                                            <span className="text-gray-400 text-[10px] mt-0.5 leading-tight">{cat.subtitle}</span>
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                    {currentCat && (
                                      <div>
                                        <p className="text-xs font-medium text-gray-500 mb-2">Précisez :</p>
                                        <div className="space-y-1">
                                          {currentCat.sub.map(sub => (
                                            <label key={sub.id}
                                              className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer border text-xs transition-all ${vueSub?.id === sub.id ? '' : 'border-transparent hover:bg-gray-100'}`}
                                              style={vueSub?.id === sub.id ? { borderColor: color, backgroundColor: color + '15' } : {}}>
                                              <input type="radio" name="vue_sub_partner" checked={vueSub?.id === sub.id}
                                                onChange={() => { setVueSub(sub); setVueExpanded(false); }}
                                                style={{ accentColor: color }} />
                                              <span className="flex-1">{sub.label}</span>
                                            </label>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          }

                          return (
                            <div key={feature.id} className="flex flex-col">
                              <button data-testid={`feature-${feature.id}`}
                                onClick={() => toggleFeature(feature.id)}
                                className={`flex items-center gap-2 px-3 py-2.5 border-2 text-sm transition-all text-left ${isSelected && (isQuantifiable || isSurface) ? 'rounded-t-lg rounded-b-none border-b-0' : 'rounded-lg'} ${isSelected ? '' : 'border-gray-200 hover:border-gray-300'}`}
                                style={isSelected ? { borderColor: color, backgroundColor: color + '10' } : {}}>
                                <span className="text-lg shrink-0">{feature.icon}</span>
                                <span className="flex-1 font-medium text-xs">{feature.label}</span>
                                {isSelected && <CheckCircle className="w-4 h-4 shrink-0" style={{ color }} />}
                              </button>
                              {isSelected && isQuantifiable && (
                                <div className="flex items-center justify-between border-2 border-t-0 rounded-b-lg px-3 py-1.5" style={{ borderColor: color, backgroundColor: color + '08' }}>
                                  <button type="button" onClick={(e) => { e.stopPropagation(); updateFeatureQuantity(feature.id, (featureQuantities[feature.id] || 1) - 1); }} className="w-7 h-7 rounded-full bg-white border flex items-center justify-center font-bold text-sm hover:bg-gray-100" style={{ borderColor: color, color }}>-</button>
                                  <span className="text-sm font-bold" style={{ color }}>{featureQuantities[feature.id] || 1}</span>
                                  <button type="button" onClick={(e) => { e.stopPropagation(); updateFeatureQuantity(feature.id, (featureQuantities[feature.id] || 1) + 1); }} className="w-7 h-7 rounded-full bg-white border flex items-center justify-center font-bold text-sm hover:bg-gray-100" style={{ borderColor: color, color }}>+</button>
                                </div>
                              )}
                              {isSelected && isSurface && (
                                <div className="flex items-center border-2 border-t-0 rounded-b-lg px-3 py-1.5 gap-2" style={{ borderColor: color, backgroundColor: color + '08' }}>
                                  <input type="number" value={featureQuantities[feature.id] || ''} onChange={(e) => updateFeatureQuantity(feature.id, parseInt(e.target.value) || 0)} onClick={(e) => e.stopPropagation()} placeholder="m²" className="w-full text-center text-sm font-bold bg-white border rounded px-2 py-1 outline-none focus:ring-1" style={{ borderColor: color }} />
                                  <span className="text-xs font-medium" style={{ color }}>m²</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Surface */}
                <Card className="border-2 shadow-lg" style={{ borderColor: color + '40' }}>
                  <CardContent className="p-6">
                    <Label className="font-semibold text-base mb-4 block">{"Surface habitable en m²"}</Label>
                    <Input type="number" value={surface} onChange={(e) => setSurface(e.target.value)}
                      placeholder="Ex: 75" className="text-lg py-6 text-center text-2xl" data-testid="estimation-surface-input" />
                  </CardContent>
                </Card>

                {['maison', 'terrain', 'local_commercial', 'bureau', 'entrepot', 'immeuble'].includes(propertyType) && (
                  <Card className="border-2 shadow-lg" style={{ borderColor: color + '40' }}>
                    <CardContent className="p-6">
                      <Label className="font-semibold text-base mb-4 block">{"Surface du terrain en m²"} <span className="text-sm font-normal text-gray-400">(facultatif)</span></Label>
                      <Input type="number" value={landSurface} onChange={(e) => setLandSurface(e.target.value)}
                        placeholder="Ex: 400" className="text-lg py-6 text-center text-2xl" data-testid="estimation-land-surface-input" />
                    </CardContent>
                  </Card>
                )}

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setEstimationStep(2)}>
                    <ArrowLeft className="w-4 h-4 mr-2" /> Retour
                  </Button>
                  <Button onClick={() => { setEstimationStep(4); getEstimation(); }}
                    disabled={!surface || estimationLoading}
                    className="text-white px-8 py-6 text-lg font-semibold" style={{ backgroundColor: color }} data-testid="estimation-next-3">
                    {estimationLoading ? 'Analyse en cours...' : <><Send className="w-5 h-5 mr-2" /> Recevoir mon estimation</>}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Résultat */}
            {estimationStep === 4 && (
              <div className="space-y-6">
                {!estimationResult ? (
                  <div className="text-center py-12">
                    <div className="w-20 h-20 rounded-full mx-auto mb-4 animate-pulse flex items-center justify-center" style={{ backgroundColor: color + '20' }}>
                      <TrendingUp className="w-10 h-10" style={{ color }} />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Analyse en cours...</h2>
                    <p className="text-gray-500 mt-2">{"Nous analysons les ventes réelles de votre quartier"}</p>
                  </div>
                ) : (
                  <>
                    <div className="text-center mb-6">
                      <div className="w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: color + '15' }}>
                        <CheckCircle className="w-10 h-10" style={{ color }} />
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900">Votre estimation</h2>
                      <p className="text-gray-500 mt-2">{address}</p>
                    </div>

                    <Card className="border-2 shadow-xl" style={{ borderColor: color }}>
                      <CardContent className="p-8">
                        <div className="grid md:grid-cols-2 gap-6">
                          <div className="text-center p-6 rounded-xl" style={{ backgroundColor: color + '10' }}>
                            <p className="text-sm text-gray-500 mb-2">{"Prix moyen au m²"}</p>
                            <p className="text-4xl font-bold" style={{ color }}>{estimationResult.price_per_m2?.toLocaleString('fr-FR')} €</p>
                            <p className="text-xs text-gray-500 mt-2">{"Basé sur " + (estimationResult.nb_transactions || 0) + " ventes DVF"}</p>
                          </div>
                          <div className="text-center p-6 rounded-xl bg-amber-50">
                            <p className="text-sm text-gray-500 mb-2">Estimation totale</p>
                            <p className="text-4xl font-bold text-amber-600">{estimationResult.estimated_price?.toLocaleString('fr-FR')} €</p>
                            <p className="text-xs text-gray-500 mt-2">{"Pour " + surface + " m²"}</p>
                          </div>
                        </div>

                        {(selectedFeatures.length > 0 || vueSub) && (
                          <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                            <p className="text-sm font-semibold mb-2">{"Caractéristiques prises en compte :"}</p>
                            <div className="flex flex-wrap gap-1.5">
                              {selectedFeatures.map(fId => {
                                const feature = (PROPERTY_FEATURES[propertyType] || []).find(f => f.id === fId);
                                if (!feature) return null;
                                return (
                                  <span key={fId} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ backgroundColor: color + '15', color }}>
                                    {feature.icon} {feature.label}
                                  </span>
                                );
                              })}
                              {vueSub && (
                                <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ backgroundColor: color + '15', color }}>
                                  🏔️ Vue : {vueSub.label}
                                </span>
                              )}
                            </div>
                          </div>
                        )}

                        <div className="mt-6 p-4 rounded-lg border" style={{ backgroundColor: color + '05', borderColor: color + '30' }}>
                          <p className="text-sm flex items-start gap-2">
                            <Award className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color }} />
                            <span>
                              <strong>{"Conseil de l'Expert :"}</strong>{" Cette estimation se base sur les données officielles DVF" +
                                (selectedFeatures.length > 0 ? ", ajustée selon les caractéristiques de votre bien" : "") +
                                ". Pour une analyse technique complète, un audit professionnel est recommandé."}
                            </span>
                          </p>
                        </div>
                      </CardContent>
                    </Card>

                    <div className="text-center space-y-4">
                      <p className="text-sm text-gray-500">{"Un email avec votre estimation a été envoyé à "}<strong>{email}</strong></p>
                      <Button onClick={() => { setEstimationResult(null); setActiveService(null); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                        className="text-white px-8 py-6 text-lg" style={{ backgroundColor: '#1e293b' }} data-testid="go-to-audit-btn">
                        <Target className="w-5 h-5 mr-2" /> {"Sélectionner Audit pour une analyse complète"}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {/* ============= AUDIT COMPLET ============= */}
        {activeService === 'audit' && !auditSubmitted && (
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: color + '15' }}>
                <Target className="w-8 h-8" style={{ color }} />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">Audit Complet</h2>
              <p className="text-gray-500 mt-2">{"Analyse technique et financière complète de votre bien immobilier"}</p>
            </div>

            <Card className="border-2 shadow-lg" style={{ borderColor: color + '40' }}>
              <CardContent className="p-6 space-y-5">
                <div>
                  <Label className="font-semibold mb-2 block">Adresse du bien *</Label>
                  <AddressInput value={auditData.address} onChange={(v) => { setAddress(v); setAuditData(prev => ({ ...prev, address: v })); setAddressValidated(false); }} onSelect={(s) => { selectAddress(s); setAuditData(prev => ({ ...prev, address: s.description || s.label || '' })); }} />
                  {auditData.address.length >= 5 && !addressValidated && (
                    <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                      <span>⚠</span> Sélectionnez une adresse dans la liste pour continuer
                    </p>
                  )}
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="font-semibold mb-2 block">{"Surface (m²)"}</Label>
                    <Input type="number" value={auditData.surface} onChange={(e) => setAuditData({ ...auditData, surface: e.target.value })} placeholder="Ex: 75" />
                  </div>
                  <div>
                    <Label className="font-semibold mb-2 block">{"Téléphone *"}</Label>
                    <Input type="tel" value={auditData.phone} onChange={(e) => setAuditData({ ...auditData, phone: e.target.value })} placeholder="06 12 34 56 78" data-testid="audit-phone-input" />
                  </div>
                </div>
                <div>
                  <Label className="font-semibold mb-2 block">Email *</Label>
                  <Input type="email" value={auditData.email} onChange={(e) => setAuditData({ ...auditData, email: e.target.value })} placeholder="votre@email.com" data-testid="audit-email-input" />
                </div>
                <div>
                  <Label className="font-semibold mb-2 block">Description du projet (optionnel)</Label>
                  <textarea value={auditData.description} onChange={(e) => setAuditData({ ...auditData, description: e.target.value })}
                    placeholder="Décrivez votre projet, votre objectif..."
                    className="w-full p-3 border rounded-lg min-h-[100px] resize-none" style={{ borderColor: color + '30' }} />
                </div>
              </CardContent>
            </Card>

            <div className="flex justify-between">
              <Button variant="outline" onClick={goBackToServices}><ArrowLeft className="w-4 h-4 mr-2" /> Retour</Button>
              <div>
                <RgpdCheckbox checked={rgpdAudit} onChange={setRgpdAudit} companyName={partner?.company_name || partner?.name} />
                <Button onClick={submitAudit} disabled={!addressValidated || !auditData.email || !auditData.phone || !rgpdAudit} className="text-white px-8 mt-2" style={{ backgroundColor: color }} data-testid="submit-audit-btn">
                  <Send className="w-4 h-4 mr-2" /> Envoyer mon dossier
                </Button>
              </div>
            </div>
          </div>
        )}
        {activeService === 'audit' && auditSubmitted && (
          <SuccessScreen title="Dossier envoyé !" message={"Votre demande d'audit a bien été envoyée. L'expert vous contactera dans les plus brefs délais."} />
        )}

        {/* ============= CONSEIL / VIP ============= */}
        {activeService === 'conseil' && (
          <div className="max-w-2xl mx-auto">
            {/* ===== PARTENAIRE : Formulaire simplifié ===== */}
            {subdomain ? (
              <div className="bg-white rounded-3xl shadow-sm border p-8 sm:p-10">
                {!partnerConseilSubmitted ? (
                  <>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: accentColor + '20' }}>
                        <Shield className="w-6 h-6" style={{ color: accentOnWhite }} />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Conseil Stratégique & Travaux</h2>
                        <p className="text-sm text-gray-500">Laissez vos coordonnées, l'agence vous contacte sous 48h</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm font-medium text-gray-700 mb-1 block">Nom <span className="text-gray-400">(optionnel)</span></Label>
                        <Input value={partnerConseilForm.name} onChange={e => setPartnerConseilForm({ ...partnerConseilForm, name: e.target.value })} placeholder="Jean Dupont" />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-700 mb-1 block">Email *</Label>
                        <Input type="email" value={partnerConseilForm.email} onChange={e => setPartnerConseilForm({ ...partnerConseilForm, email: e.target.value })} placeholder="votre@email.com" data-testid="partner-conseil-email" />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-gray-700 mb-1 block">Téléphone *</Label>
                        <Input type="tel" value={partnerConseilForm.phone} onChange={e => setPartnerConseilForm({ ...partnerConseilForm, phone: e.target.value })} placeholder="06 12 34 56 78" data-testid="partner-conseil-phone" />
                      </div>
                      <RgpdCheckbox checked={rgpdPartnerConseil} onChange={setRgpdPartnerConseil} companyName={partner?.company_name || partner?.name} />
                      <button onClick={submitPartnerConseil} disabled={partnerConseilLoading || !rgpdPartnerConseil}
                        className="w-full py-3.5 rounded-xl text-sm font-semibold transition-all hover:opacity-90 mt-2 disabled:opacity-50"
                        style={{ backgroundColor: color, color: colorContrast }}
                        data-testid="partner-conseil-submit">
                        {partnerConseilLoading ? 'Envoi...' : "Être contacté par l'agence"}
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <CheckCircle className="w-14 h-14 mx-auto mb-4" style={{ color: colorOnWhite }} />
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Demande envoyée !</h3>
                    <p className="text-gray-500">L'agence vous contactera sous 48h.</p>
                    <button onClick={() => { setActiveService(null); setPartnerConseilSubmitted(false); setPartnerConseilForm({ email: '', phone: '', name: '' }); }}
                      className="mt-6 text-sm underline" style={{ color: colorOnWhite }}>
                      Retour à la page
                    </button>
                  </div>
                )}
              </div>
            ) : (
              /* ===== L'ŒIL DU MARCHAND DIRECT : Formulaire complet VIP (inchangé) ===== */
              <div>
            {/* Choice screen */}
            {!vipSubMode && !chantierSubmitted && !conseilSubmitted && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: color + '15' }}>
                    <Shield className="w-8 h-8" style={{ color }} />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Accompagnement VIP</h2>
                  <p className="text-gray-500 mt-2">{"Choisissez le type d'accompagnement dont vous avez besoin"}</p>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <button data-testid="vip-choice-travaux" onClick={() => setVipSubMode('travaux')}
                    className="p-6 rounded-2xl border-2 bg-white hover:shadow-lg transition-all text-left" style={{ borderColor: color + '30' }}
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = color; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = color + '30'; }}>
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: color + '15' }}>
                      <Shield className="w-6 h-6" style={{ color }} />
                    </div>
                    <h3 className="text-xl font-bold mb-2">Suivi de Travaux</h3>
                    <p className="text-sm text-gray-500 mb-4">{"Rénovation ? Je vous accompagne de A à Z : devis, suivi chantier, réception."}</p>
                    <ul className="space-y-2 text-sm">
                      {['+50 artisans partenaires', "Économies jusqu'à 20%", "Tous corps d'état"].map(t => (
                        <li key={t} className="flex items-center gap-2 text-gray-600"><Check className="w-4 h-4" style={{ color }} />{t}</li>
                      ))}
                    </ul>
                    <div className="mt-4 flex items-center gap-2 font-semibold" style={{ color }}>Choisir <ArrowRight className="w-4 h-4" /></div>
                  </button>

                  <button data-testid="vip-choice-conseil" onClick={() => setVipSubMode('conseil')}
                    className="p-6 rounded-2xl border-2 bg-white hover:shadow-lg transition-all text-left border-gray-200"
                    onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#1e293b'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#e5e7eb'; }}>
                    <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                      <Award className="w-6 h-6 text-gray-700" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">{"Conseil Stratégique"}</h3>
                    <p className="text-sm text-gray-500 mb-4">{"Besoin d'un avis expert ? Session de conseil personnalisé pour vos projets."}</p>
                    <ul className="space-y-2 text-sm">
                      {["25 ans d'expérience", 'Analyse personnalisée', 'Accompagnement sur mesure'].map(t => (
                        <li key={t} className="flex items-center gap-2 text-gray-600"><Check className="w-4 h-4 text-emerald-600" />{t}</li>
                      ))}
                    </ul>
                    <div className="mt-4 flex items-center gap-2 text-gray-800 font-semibold">Choisir <ArrowRight className="w-4 h-4" /></div>
                  </button>
                </div>
                <div className="text-center">
                  <Button variant="outline" onClick={goBackToServices}><ArrowLeft className="w-4 h-4 mr-2" /> Retour aux services</Button>
                </div>
              </div>
            )}

            {/* Travaux form */}
            {vipSubMode === 'travaux' && !chantierSubmitted && (
              <div className="space-y-6">
                <Button variant="ghost" onClick={() => setVipSubMode(null)} className="mb-2"><ArrowLeft className="w-4 h-4 mr-2" /> Retour aux choix</Button>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: color + '15' }}>
                    <Shield className="w-8 h-8" style={{ color }} />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Suivi de Travaux VIP</h2>
                </div>
                <Card className="border-2 shadow-lg" style={{ borderColor: color + '40' }}>
                  <CardContent className="p-6 space-y-5">
                    <div>
                      <Label className="font-semibold mb-2 block">Adresse du projet *</Label>
                      <Input value={chantierData.address} onChange={(e) => setChantierData({ ...chantierData, address: e.target.value })} placeholder="45 Rue de la République, 69001 Lyon" data-testid="chantier-address-input" />
                    </div>
                    <div>
                      <Label className="font-semibold mb-2 block">{"Budget travaux estimé *"}</Label>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {BUDGET_OPTIONS.map((option) => (
                          <button key={option.value} data-testid={`budget-${option.value}`}
                            onClick={() => setChantierData({ ...chantierData, budget: option.value })}
                            className={`p-3 rounded-xl border-2 transition-all text-center text-sm font-medium ${chantierData.budget === option.value ? 'ring-2' : 'border-gray-200 hover:border-gray-300'}`}
                            style={chantierData.budget === option.value ? { borderColor: color, backgroundColor: color + '10', boxShadow: `0 0 0 2px ${color}` } : {}}>
                            {option.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="font-semibold mb-2 block">Description (optionnel)</Label>
                      <textarea value={chantierData.description} onChange={(e) => setChantierData({ ...chantierData, description: e.target.value })}
                        placeholder={"Décrivez votre projet de rénovation..."} className="w-full p-3 border rounded-lg min-h-[80px] resize-none" style={{ borderColor: color + '30' }} data-testid="chantier-description" />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="font-semibold mb-2 block">{"Téléphone *"}</Label>
                        <Input type="tel" value={chantierData.phone} onChange={(e) => setChantierData({ ...chantierData, phone: e.target.value })} placeholder="06 12 34 56 78" data-testid="chantier-phone-input" />
                      </div>
                      <div>
                        <Label className="font-semibold mb-2 block">Email *</Label>
                        <Input type="email" value={chantierData.email} onChange={(e) => setChantierData({ ...chantierData, email: e.target.value })} placeholder="votre@email.com" data-testid="chantier-email-input" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <div className="flex justify-end">
                  <Button onClick={submitChantier} className="text-white px-8" style={{ backgroundColor: color }} data-testid="submit-chantier-btn">
                    <Send className="w-4 h-4 mr-2" /> Envoyer ma demande
                  </Button>
                </div>
              </div>
            )}
            {chantierSubmitted && <SuccessScreen title="Demande envoyée !" message={"L'expert vous contactera sous 24h pour organiser le suivi de votre chantier."} />}

            {/* Conseil form */}
            {vipSubMode === 'conseil' && !conseilSubmitted && (
              <div className="space-y-6">
                <Button variant="ghost" onClick={() => setVipSubMode(null)} className="mb-2"><ArrowLeft className="w-4 h-4 mr-2" /> Retour aux choix</Button>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center bg-gray-100">
                    <Award className="w-8 h-8 text-gray-700" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">{"Conseil Stratégique"}</h2>
                </div>
                <Card className="border-2 shadow-lg" style={{ borderColor: color + '40' }}>
                  <CardContent className="p-6 space-y-5">
                    <div>
                      <Label className="font-semibold mb-2 block">Type de conseil *</Label>
                      <div className="grid grid-cols-2 gap-3">
                        {[
                          { id: 'audit_devis', label: 'Audit de devis' },
                          { id: 'faisabilite', label: 'Étude de faisabilité' },
                          { id: 'mediation', label: 'Médiation / Litige' },
                          { id: 'strategie_mdb', label: "Stratégie d'investissement" },
                        ].map(type => (
                          <button key={type.id} onClick={() => setConseilData({ ...conseilData, conseil_type: type.id })}
                            className={`p-3 rounded-xl border-2 text-sm font-medium transition-all ${conseilData.conseil_type === type.id ? 'ring-1' : 'border-gray-200 hover:border-gray-300'}`}
                            style={conseilData.conseil_type === type.id ? { borderColor: color, backgroundColor: color + '10' } : {}}>
                            {type.label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="font-semibold mb-2 block">Décrivez votre besoin *</Label>
                      <textarea value={conseilData.description} onChange={(e) => setConseilData({ ...conseilData, description: e.target.value })}
                        placeholder={"Décrivez votre situation et votre besoin..."}
                        className="w-full p-3 border rounded-lg min-h-[100px] resize-none" style={{ borderColor: color + '30' }} data-testid="conseil-description" />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label className="font-semibold mb-2 block">Email *</Label>
                        <Input type="email" value={conseilData.email} onChange={(e) => setConseilData({ ...conseilData, email: e.target.value })} placeholder="votre@email.com" data-testid="conseil-email-input" />
                      </div>
                      <div>
                        <Label className="font-semibold mb-2 block">{"Téléphone *"}</Label>
                        <Input type="tel" value={conseilData.phone} onChange={(e) => setConseilData({ ...conseilData, phone: e.target.value })} placeholder="06 12 34 56 78" data-testid="conseil-phone-input" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                <div className="flex justify-end">
                  <Button onClick={submitConseil} className="text-white px-8" style={{ backgroundColor: color }} data-testid="submit-conseil-btn">
                    <Send className="w-4 h-4 mr-2" /> Envoyer ma demande
                  </Button>
                </div>
              </div>
            )}
            {conseilSubmitted && <SuccessScreen title="Demande envoyée !" message={"L'expert étudiera votre dossier et vous recontactera sous 48h."} />}
              </div>
            )} {/* fin du else L'Œil du Marchand direct */}
          </div>
        )}
      </main>
      )}

    </div>
  );
}
